import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { cleanupUserSettings } from "../store/slice/auth-slice";

/**
 * Shared logout handler — used by both the Header dropdown and the
 * mobile sidebar's logout list-item. Previously each file inlined the
 * same `dispatch(cleanupUserSettings()) + navigate("/login")` pair,
 * making it easy for one to drift behind the other (e.g. one adding
 * socket teardown without the other).
 *
 * Returns a stable `logout()` function (memoized via useCallback) so
 * callers can pass it as a prop without triggering child re-renders.
 *
 * If/when the app gains additional teardown steps (closing
 * WebSockets, revoking the token server-side, clearing IndexedDB,
 * etc.) they go HERE — both call sites pick them up automatically.
 */
const useLogout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  return useCallback(() => {
    // 1. Clear Redux state. The reducer also strips the localStorage
    //    token internally so subsequent API calls won't authenticate
    //    as the just-logged-out user.
    dispatch(cleanupUserSettings());

    // 2. Hard navigate to the login page. We use react-router's
    //    `navigate` rather than `window.location.href` so the SPA
    //    state isn't blown away (route guards on /login mount fresh).
    navigate("/login");
  }, [dispatch, navigate]);
};

export default useLogout;
